/**
 * Gem Stage Route
 *
 * Triggers loading of the gem stage when this route becomes active.
 * The actual canvas is in the parent layout (ux.tsx).
 *
 * When a non-Babylon viewer is selected, the BabylonJS stage load is skipped
 * and a lazy-loaded Three.js or model-viewer component renders the GLB instead.
 */

import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { useViewerRefs } from "@/babylon/context/ViewerRefsContext";
import { resolveGemGlbUrl } from "@/babylon/utils/glbPaths";
import { ColorScheme, useTheme } from "@/context/ThemeContext";
import { useViewer } from "@/context/ViewerContext";
import { useLoadStageOnMount } from "../3d";

const ThreeGlbViewer = lazy(
  () => import("@/babylon/components/viewers/ThreeGlbViewer"),
);
const ModelViewerGlb = lazy(
  () => import("@/babylon/components/viewers/ModelViewerGlb"),
);

export const Route = createFileRoute("/_immersive/demo/3d/gem")({
  component: GemStageRoute,
});

function GemStageRoute() {
  const { viewerType } = useViewer();
  const { setThreeRefs, setModelViewerElement } = useViewerRefs();
  const { activeTheme, activeColorScheme } = useTheme();
  const glbUrl = resolveGemGlbUrl("default");
  const gemTint = {
    isDark: activeColorScheme === ColorScheme.DARK,
    theme: activeTheme,
  };

  useLoadStageOnMount("gem", undefined, viewerType === "babylon");

  if (viewerType === "babylon") return null;

  return (
    <div className="w-full h-full pointer-events-auto">
      <Suspense
        fallback={
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            Loading viewer...
          </div>
        }
      >
        {viewerType === "three.js" && (
          <ThreeGlbViewer
            gemTint={gemTint}
            glbUrl={glbUrl}
            onRefsReady={setThreeRefs}
          />
        )}
        {viewerType === "model-viewer" && (
          <ModelViewerGlb
            gemTint={gemTint}
            glbUrl={glbUrl}
            onElementReady={setModelViewerElement}
          />
        )}
      </Suspense>
    </div>
  );
}
